import { ReactElement, useEffect } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useLocation, useNavigate } from 'react-router-dom';

import Avatar from 'components/Avatar';
import { IconCaretLeft, IconClose } from 'components/Icons';
import Typography from 'components/Typography';

import { useAnalytics } from 'hooks/useAnalytics';
import { useConversation } from 'hooks/conversation';
import { useView } from 'hooks/view';

import { CHATUI_WINDOW_PATH } from 'constants/global';
import { ACTIONS } from 'constants/hook';
import { ROUTES } from 'routes/constants';

import { ANALYTICS } from 'analytics/constants';
import { CHAT_UI_PREFIX } from 'constants/id';
import { AnalyticClickedDetailsType } from 'analytics/types';
import { htmlId } from 'utils/html';
import { getIsInAssignmentQueue } from 'utils/message';

import { endAdvocateChat, cancelEndChatRequest } from './content';
import {
  StyledAvatarImage,
  StyledAvatarWrapper,
  StyledEndChatButton,
  StyledHeaderContainer,
  StyledIconWrapper,
  StyledMainContentWrapper,
} from './Header.styles';
import { HeaderProps } from './Header.types';
import { CANCEL_ESCALATION, END_CHAT } from 'components/Layout/EndAdvocateChatModal/content';
import { getIsAdvocateChatEndedByUser } from 'utils/message/message';

const Header = ({
  id,
  endChatModalType,
  hideEndChatButton,
  handleHideEndChat,
  showEndChatModal,
  toggleEndChatModal,
  handleEndChatModalType,
}: HeaderProps): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { resetBoundary } = useErrorBoundary();
  const { dispatchViewAction, viewState } = useView();
  const { conversationState, dispatchConversationAction } = useConversation();
  const {
    isConnected,
    participants: { advocates, system },
    messages,
  } = conversationState;
  const hasActiveAdvocates = Boolean(advocates.length);
  const buttonText = endChatModalType === END_CHAT ? endAdvocateChat : cancelEndChatRequest;

  useEffect(() => {
    if (hasActiveAdvocates) {
      const isChatEndedByUser = getIsAdvocateChatEndedByUser(messages);
      handleHideEndChat(isChatEndedByUser);
      handleEndChatModalType(END_CHAT);
      showEndChatModal && toggleEndChatModal();
    }
  }, [hasActiveAdvocates]);

  useEffect(() => {
    if (isConnected && !hasActiveAdvocates) {
      if (messages?.length && getIsInAssignmentQueue(messages)) {
        handleHideEndChat(false);
        handleEndChatModalType(CANCEL_ESCALATION);
      } else {
        !hideEndChatButton && handleHideEndChat(true);
      }
    }
  }, [messages]);

  const isHomeRouteNoError =
    location.pathname === ROUTES.HOME && !viewState.errorBoundary.isVisible;

  const closeErrorViewIfVisible = () => {
    if (viewState.errorBoundary.isVisible) {
      dispatchViewAction({
        type: ACTIONS.CHANGE,
        payload: {
          ...viewState,
          errorBoundary: {
            ...viewState.errorBoundary,
            isVisible: false,
          },
        },
      });
      resetBoundary();
    }
  };

  const resetInactivityAndSyncMessages = async () => {
    // prevent flash content reload when viewing chat again
    const messages = await window[CHATUI_WINDOW_PATH].getMessages();
    dispatchConversationAction({
      type: ACTIONS.CHANGE,
      payload: { messages, isAnimateLastMessage: false },
    });
  };

  const handleOnClickBackwards = () => {
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.CLICKED,
      details: {
        analytic_click_id: `${CHAT_UI_PREFIX}-navigate-backwards`,
        content: '<',
      } as AnalyticClickedDetailsType,
    });
    resetInactivityAndSyncMessages();
    closeErrorViewIfVisible();
    navigate(ROUTES.HOME);
  };

  const handleOnClickCloseButton = () => {
    trackEvent({ analyticEvent: ANALYTICS.EVENT.CHAT_CLOSED });
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.CLICKED,
      details: {
        analytic_click_id: `${CHAT_UI_PREFIX}-navigate-close-chat-button`,
        content: 'X',
      } as AnalyticClickedDetailsType,
    });
    resetInactivityAndSyncMessages();
    closeErrorViewIfVisible();
    window[CHATUI_WINDOW_PATH].hide();
  };

  const handleOnClickEndAdvocateChatButton = () => {
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.CLICKED,
      details: {
        analytic_click_id: `${CHAT_UI_PREFIX}-end-advocate-chat-button`,
        content: buttonText,
      },
    });
    toggleEndChatModal();
  };

  const getOrderedParticipantsList = () => {
    const lastAssignedAdvocates = advocates.reverse();
    const orderedParticipants = [...lastAssignedAdvocates, ...system];

    // wait for connection to Twilio to finish loading
    if (!orderedParticipants.length) return;

    const displayParticipant = orderedParticipants[0];
    return (
      <StyledAvatarWrapper className='avatar-wrapper'>
        <StyledAvatarImage>
          <Avatar
            participantName={displayParticipant.name}
            key={displayParticipant.identity}
            showPresence={true}
            size='2rem'
          />
        </StyledAvatarImage>
        <Typography as='h2' color='inverse' variant='heading' size='sm'>
          {displayParticipant.name}
        </Typography>
      </StyledAvatarWrapper>
    );
  };

  const CommonCloseChatButton = (
    <StyledIconWrapper
      id={htmlId('navigate-close-chat-button-container')}
      onClick={handleOnClickCloseButton}
    >
      <IconClose id={htmlId('navigate-close-chat-button')} />
    </StyledIconWrapper>
  );

  const EndAdvocateChatButton = () => {
    if (hideEndChatButton) return null;

    const buttonId =
      endChatModalType === END_CHAT
        ? 'end-advocate-chat-button'
        : 'end-advocate-chat-cancel-request-button';
    return (
      <StyledEndChatButton id={htmlId(buttonId)} onClick={handleOnClickEndAdvocateChatButton}>
        {buttonText}
      </StyledEndChatButton>
    );
  };

  if (isHomeRouteNoError) {
    return (
      <StyledHeaderContainer id={id}>
        <StyledMainContentWrapper />
        {CommonCloseChatButton}
      </StyledHeaderContainer>
    );
  } else {
    return (
      <StyledHeaderContainer id={id}>
        <StyledIconWrapper
          id={htmlId('navigate-backwards-button-container')}
          onClick={handleOnClickBackwards}
        >
          <IconCaretLeft id={htmlId('navigate-backwards-button')} />
        </StyledIconWrapper>
        <StyledMainContentWrapper
          id={htmlId('header-ordered-participants-container')}
          $hideEndChat={hideEndChatButton}
        >
          {getOrderedParticipantsList()}
        </StyledMainContentWrapper>
        <EndAdvocateChatButton />
        {CommonCloseChatButton}
      </StyledHeaderContainer>
    );
  }
};

export default Header;
